import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Box, Typography, Grid, Stack, Button,
  TextField, IconButton, Alert, Chip, MenuItem, alpha, useTheme,
  InputAdornment, Tooltip, LinearProgress, Tabs, Tab, Card,
  FormControlLabel, Switch,
} from '@mui/material';
import {
  Image as ImageIcon, Search as SearchIcon, Globe, Trash2, Send, FileText,
  Layers, CheckCircle2,
} from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import axios from '../utils/axios';
import { getBlogMainImageUrl, getBlogContentImageUrl } from '../utils/helpers';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from './common/modal';

const SITE = 'https://www.resellerbazzar.in';

// Derive a URL-safe slug from a string.
const slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

// 'table' (+ the table module below) keeps tables intact: without it Quill
// flattened every <table> into loose paragraphs on the first save. Quill 2 has
// no 'bullet' format (bullets are a 'list' value) — it only logged a warning.
const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'indent', 'link', 'image', 'align', 'color', 'background', 'table',
];

// Quill 2.0.3's getSemanticHTML() emits every space as &nbsp;, so saved posts
// had no line-break points (words split mid-word on phones). Store real spaces.
// Applied at SAVE time only: feeding it back through onChange would make
// react-quill re-set the editor on every keystroke (and eat trailing spaces).
const normalizeQuillHtml = (html) => (typeof html === 'string' ? html.replace(/&nbsp;/g, ' ') : html);

const EMPTY = {
  title: '', url: '', category_name: '', keywords: '', description: '',
  content: '', seo_title: '', canonical_url: '', og_image: '', image_alt: '',
  author_name: 'Reseller Bazzar Editorial Team', author_url: '', status: 'published', noindex: false,
  blocks: [],
};

const heroImageUrl = (filename) => {
  return getBlogMainImageUrl(filename);
};

// Small helper to render a character counter with an ideal-length hint.
const CharCount = ({ value, ideal }) => {
  const len = (value || '').length;
  const over = len > ideal;
  return (
    <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, color: over ? 'warning.main' : 'text.disabled' }}>
      {len}/{ideal}
    </Typography>
  );
};

const SectionLabel = ({ children }) => (
  <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary', letterSpacing: 1, textTransform: 'uppercase', mb: 1.5 }}>
    {children}
  </Typography>
);

const BlogModal = ({ open, onClose, blog, onSuccess }) => {
  const theme = useTheme();
  const isEdit = Boolean(blog?._id);
  const quillRef = useRef(null);
  const slugEdited = useRef(false);

  const [form, setForm] = useState(EMPTY);
  const [contentTab, setContentTab] = useState(0);
  const [heroFile, setHeroFile] = useState(null);
  const [heroPreview, setHeroPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    slugEdited.current = isEdit; // don't auto-rewrite an existing slug
    setError('');
    setHeroFile(null);
    setContentTab(0);
    if (blog) {
      setForm({ ...EMPTY, ...blog, status: blog.status || 'published', blocks: Array.isArray(blog.blocks) ? blog.blocks : [] });
      setHeroPreview(heroImageUrl(blog.blog_main_image));
    } else {
      setForm(EMPTY);
      setHeroPreview('');
    }
  }, [open, blog, isEdit]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleTitle = (value) => {
    setForm((f) => {
      const next = { ...f, title: value };
      if (!slugEdited.current) next.url = slugify(value);
      return next;
    });
  };

  const handleSlug = (value) => {
    slugEdited.current = true;
    set('url', slugify(value));
  };

  const handleHeroFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setHeroFile(file);
    setHeroPreview(URL.createObjectURL(file));
  };

  // Custom Quill image handler — uploads to the backend and inserts a real URL
  // (instead of bloating the content with a base64 data URI).
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setUploadingImg(true);
      try {
        const fd = new FormData();
        fd.append('blog_content_images', file);
        const res = await axios.post('/admin/blogs/content-image', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          skipNotify: true, // background upload on image insert — no toast
        });
        const url = getBlogContentImageUrl(res.data.url);
        const editor = quillRef.current?.getEditor?.();
        const range = editor?.getSelection(true);
        editor?.insertEmbed(range ? range.index : 0, 'image', url, 'user');
        if (range) editor?.setSelection(range.index + 1);
      } catch (err) {
        setError('Failed to upload image. Please try again.');
      } finally {
        setUploadingImg(false);
      }
    };
  }, []);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, 4, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        [{ color: [] }, { background: [] }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: { image: imageHandler },
    },
    table: true,
  }), [imageHandler]);

  const save = async (overrideStatus) => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    if (!form.url.trim()) { setError('URL slug is required.'); return; }
    const statusToSave = overrideStatus || form.status || 'published';
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      [
        'title', 'url', 'category_name', 'keywords', 'description', 'content',
        'seo_title', 'canonical_url', 'og_image', 'image_alt', 'author_name', 'author_url',
      ].forEach((k) => fd.append(k, (k === 'content' ? normalizeQuillHtml(form[k] ?? '') : (form[k] ?? '')).toString()));
      fd.append('status', statusToSave);
      fd.append('noindex', form.noindex ? 'true' : 'false');
      if (form.blocks && Array.isArray(form.blocks) && form.blocks.length > 0) {
        fd.append('blocks', JSON.stringify(form.blocks));
      }
      if (heroFile) {
        // A newly chosen file — backend converts it to .webp and stores it.
        fd.append('blog_main_image', heroFile);
      } else {
        // No new file: send the current filename to keep it, or '' to clear it.
        fd.append('blog_main_image', form.blog_main_image || '');
      }

      if (isEdit) {
        await axios.put(`/admin/blogs/${blog._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, skipNotify: 'success' });
      } else {
        await axios.post('/admin/blogs', fd, { headers: { 'Content-Type': 'multipart/form-data' }, skipNotify: 'success' });
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to save blog');
    } finally {
      setSaving(false);
    }
  };

  const previewTitle = form.seo_title?.trim() || form.title || 'Blog title preview';
  const canonicalPreview = (form.canonical_url?.trim() || `${SITE}/blog/${form.url || 'your-slug'}`);

  return (
    <AdminDialog open={open} onClose={!saving ? onClose : undefined} maxWidth="lg">
      <AdminDialogHeader
        title={isEdit ? 'Edit Blog' : 'New Blog'}
        subtitle="Full content & SEO control — title, slug, meta, social image and structured author info."
        icon={FileText}
        onClose={!saving ? onClose : undefined}
      >
        <Chip
          size="small"
          label={form.status === 'draft' ? 'Draft' : 'Published'}
          sx={{
            fontWeight: 800,
            bgcolor: alpha(form.status === 'draft' ? theme.palette.warning.main : theme.palette.success.main, 0.14),
            color: form.status === 'draft' ? theme.palette.warning.dark : theme.palette.success.dark,
          }}
        />
      </AdminDialogHeader>

      {(saving || uploadingImg) && <LinearProgress />}

      <AdminDialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Grid container spacing={3}>
          {/* ───────── Main content column ───────── */}
          <Grid size={{ xs: 12, md: 8 }}>
            <SectionLabel>Content</SectionLabel>
            <Stack spacing={2}>
              <Box>
                <TextField
                  fullWidth label="Title" value={form.title}
                  onChange={(e) => handleTitle(e.target.value)}
                  helperText={<>Headline shown on the blog. <CharCount value={form.title} ideal={60} /></>}
                />
              </Box>

              <TextField
                fullWidth label="URL slug" value={form.url}
                onChange={(e) => handleSlug(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">/blog/</InputAdornment> }}
                helperText="Auto-generated from the title; edit to customise. Changing it on an existing post changes its public link."
              />

              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Content</Typography>
                  {form.blocks && form.blocks.length > 0 && (
                    <Tabs
                      value={contentTab}
                      onChange={(e, v) => setContentTab(v)}
                      sx={{
                        minHeight: 32,
                        '& .MuiTab-root': {
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          minHeight: 32,
                          py: 0.25,
                          px: 1.5,
                        }
                      }}
                    >
                      <Tab label="Rich Text Editor" />
                      <Tab
                        icon={<Layers size={14} />}
                        iconPosition="start"
                        label={`Block Sections (${form.blocks.length})`}
                      />
                    </Tabs>
                  )}
                </Stack>

                {contentTab === 0 ? (
                  <>
                    <Box sx={{
                      '& .ql-toolbar': { borderTopLeftRadius: 8, borderTopRightRadius: 8, bgcolor: 'background.neutral', borderColor: 'divider' },
                      '& .ql-container': { borderBottomLeftRadius: 8, borderBottomRightRadius: 8, borderColor: 'divider', fontSize: 15 },
                      '& .ql-editor': { minHeight: 320 },
                    }}>
                      <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={form.content}
                        onChange={(content) => set('content', content)}
                        modules={quillModules}
                        formats={QUILL_FORMATS}
                      />
                    </Box>
                    <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 0.75 }}>
                      Use the image button in the toolbar to upload images — they’re stored on the server and inserted as proper URLs.
                    </Typography>
                  </>
                ) : (
                  <Stack spacing={1.5} sx={{ maxHeight: 520, overflowY: 'auto', pr: 0.5 }}>
                    <Alert severity="info" sx={{ py: 0.5, borderRadius: 2, fontSize: 12.5 }}>
                      Structured block layout generated by Gemini Pro AI. Any HTML edits in the Rich Text Editor are preserved upon saving.
                    </Alert>

                    {form.blocks.map((blk, idx) => {
                      const type = blk.type || 'section';
                      const badgeColor =
                        type === 'hero' ? 'primary' :
                        type === 'key_takeaways' ? 'success' :
                        type === 'callout' ? 'warning' :
                        type === 'faq' ? 'info' :
                        type === 'cta' ? 'secondary' : 'default';

                      return (
                        <Card key={idx} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Chip
                              size="small"
                              label={`#${idx + 1} ${type.replace('_', ' ').toUpperCase()}`}
                              color={badgeColor}
                              sx={{ fontWeight: 800, fontSize: 10.5, height: 22 }}
                            />
                            {type === 'callout' && blk.callout_type && (
                              <Chip size="small" variant="outlined" label={blk.callout_type} sx={{ height: 20, fontSize: 10 }} />
                            )}
                          </Stack>

                          {type === 'hero' && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{blk.title}</Typography>
                              {blk.subtitle && <Typography variant="caption" color="text.secondary">{blk.subtitle}</Typography>}
                              {blk.read_time && <Chip size="small" label={blk.read_time} sx={{ mt: 1, height: 20, fontSize: 11 }} />}
                            </Box>
                          )}

                          {type === 'key_takeaways' && (
                            <Box sx={{ bgcolor: (t) => alpha(t.palette.success.main, 0.06), p: 1.5, borderRadius: 1.5 }}>
                              <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 1, color: 'success.dark' }}>
                                Key Takeaways
                              </Typography>
                              <Stack spacing={0.75}>
                                {(blk.points || []).map((pt, pIdx) => (
                                  <Stack key={pIdx} direction="row" spacing={1} alignItems="flex-start">
                                    <CheckCircle2 size={15} color="#2e7d32" style={{ marginTop: 2, flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: 12 }}>{pt}</Typography>
                                  </Stack>
                                ))}
                              </Stack>
                            </Box>
                          )}

                          {type === 'section' && (
                            <Box>
                              {blk.heading && <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>{blk.heading}</Typography>}
                              {blk.content && <Typography sx={{ fontSize: 12.5, color: 'text.secondary', whiteSpace: 'pre-line' }}>{blk.content}</Typography>}
                              {blk.bullet_points && blk.bullet_points.length > 0 && (
                                <Box component="ul" sx={{ pl: 2, m: 0, mt: 1 }}>
                                  {blk.bullet_points.map((pt, bIdx) => (
                                    <Typography component="li" key={bIdx} sx={{ fontSize: 12, color: 'text.secondary' }}>{pt}</Typography>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          )}

                          {type === 'callout' && (
                            <Box sx={{ bgcolor: (t) => alpha(t.palette.warning.main, 0.08), p: 1.5, borderRadius: 1.5, borderLeft: '4px solid #ed6c02' }}>
                              {blk.title && <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'warning.dark' }}>{blk.title}</Typography>}
                              <Typography sx={{ fontSize: 12, mt: 0.5, color: 'text.primary' }}>{blk.text}</Typography>
                            </Box>
                          )}

                          {type === 'faq' && (
                            <Box>
                              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'primary.main' }}>Q: {blk.question}</Typography>
                              <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>{blk.answer}</Typography>
                            </Box>
                          )}

                          {type === 'cta' && (
                            <Box sx={{ bgcolor: (t) => alpha(t.palette.secondary.main, 0.08), p: 1.5, borderRadius: 1.5, textAlign: 'center' }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{blk.title}</Typography>
                              {blk.description && <Typography sx={{ fontSize: 12, color: 'text.secondary', my: 0.5 }}>{blk.description}</Typography>}
                              {blk.button_text && (
                                <Chip label={blk.button_text} color="primary" size="small" sx={{ fontWeight: 700, mt: 0.5 }} />
                              )}
                            </Box>
                          )}
                        </Card>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Grid>

          {/* ───────── Sidebar: meta, image, SEO ───────── */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={2.5}>
              {/* Publish + organise */}
              <Box>
                <SectionLabel>Publish</SectionLabel>
                <Stack spacing={2}>
                  <TextField
                    select fullWidth label="Status" value={form.status}
                    onChange={(e) => set('status', e.target.value)}
                  >
                    <MenuItem value="published">Published (visible on site)</MenuItem>
                    <MenuItem value="draft">Draft (hidden from site & SEO)</MenuItem>
                  </TextField>
                  <TextField fullWidth label="Category" value={form.category_name} onChange={(e) => set('category_name', e.target.value)} placeholder="e.g. Marketing" />
                </Stack>
              </Box>

              {/* Hero image */}
              <Box>
                <SectionLabel>Hero Image</SectionLabel>
                <Box
                  sx={{
                    border: '1px dashed', borderColor: 'divider', borderRadius: 2, p: 1.5,
                    bgcolor: 'background.neutral', textAlign: 'center',
                  }}
                >
                  {heroPreview ? (
                    <Box sx={{ position: 'relative' }}>
                      <Box component="img" src={heroPreview} alt="hero preview"
                        sx={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 1.5, display: 'block' }} />
                      <Tooltip title="Remove image">
                        <IconButton
                          size="small" color="error"
                          onClick={() => { setHeroFile(null); setHeroPreview(''); set('blog_main_image', ''); }}
                          sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper' } }}
                        >
                          <Trash2 size={15} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Stack alignItems="center" spacing={1} sx={{ py: 2, color: 'text.disabled' }}>
                      <ImageIcon size={26} />
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>No image selected</Typography>
                    </Stack>
                  )}
                  <Button component="label" size="small" startIcon={<ImageIcon size={15} />} sx={{ mt: 1.5, fontWeight: 700 }}>
                    {heroPreview ? 'Replace image' : 'Upload image'}
                    <input hidden type="file" accept="image/*" onChange={handleHeroFile} />
                  </Button>
                </Box>
                <TextField
                  fullWidth size="small" label="Image alt text" value={form.image_alt}
                  onChange={(e) => set('image_alt', e.target.value)} sx={{ mt: 1.5 }}
                  helperText="Describes the image for accessibility & image SEO."
                />
              </Box>

              {/* SEO */}
              <Box>
                <SectionLabel>SEO</SectionLabel>
                <Stack spacing={2}>
                  <TextField
                    fullWidth label="SEO title" value={form.seo_title}
                    onChange={(e) => set('seo_title', e.target.value)}
                    helperText={<>Overrides the &lt;title&gt; tag. <CharCount value={form.seo_title || form.title} ideal={60} /></>}
                  />
                  <TextField
                    fullWidth multiline minRows={2} label="Meta description" value={form.description}
                    onChange={(e) => set('description', e.target.value)}
                    helperText={<>Shown in search results & social shares. <CharCount value={form.description} ideal={160} /></>}
                  />
                  <TextField
                    fullWidth label="Keywords" value={form.keywords}
                    onChange={(e) => set('keywords', e.target.value)}
                    helperText="Comma-separated focus keywords."
                  />
                  <TextField
                    fullWidth label="Canonical URL" value={form.canonical_url}
                    onChange={(e) => set('canonical_url', e.target.value)}
                    placeholder={`${SITE}/blog/${form.url || 'slug'}`}
                    helperText="Optional. Leave blank to use the default blog URL."
                  />
                  <TextField
                    fullWidth label="OG / social image URL" value={form.og_image || ''}
                    onChange={(e) => set('og_image', e.target.value)}
                    helperText="Optional. Falls back to the hero image."
                  />
                </Stack>
              </Box>

              {/* Author */}
              <Box>
                <SectionLabel>Author</SectionLabel>
                <Stack spacing={2}>
                  <TextField fullWidth label="Author name" value={form.author_name} onChange={(e) => set('author_name', e.target.value)} />
                  <TextField fullWidth label="Author URL" value={form.author_url} onChange={(e) => set('author_url', e.target.value)} placeholder={`${SITE}/about`} />
                </Stack>
              </Box>

              {/* Search visibility */}
              <Box>
                <SectionLabel>Search visibility</SectionLabel>
                <FormControlLabel
                  control={<Switch checked={!!form.noindex} onChange={(e) => set('noindex', e.target.checked)} />}
                  label="Hide from Google (noindex)"
                />
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  The post stays readable on the site but is kept out of search results and the sitemap. Use for off-topic posts.
                </Typography>
              </Box>

              {/* Live SERP preview */}
              <Box>
                <SectionLabel>Search Preview</SectionLabel>
                <Box sx={{ p: 1.75, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper' }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary', mb: 0.5 }}>
                    <Globe size={13} />
                    <Typography noWrap sx={{ fontSize: 12 }}>{canonicalPreview}</Typography>
                  </Stack>
                  <Typography noWrap sx={{ fontSize: 16, fontWeight: 600, color: theme.palette.mode === 'dark' ? '#8ab4f8' : '#1a0dab', lineHeight: 1.3 }}>
                    {previewTitle}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {form.description || 'Add a meta description to control how this post appears in search results.'}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </AdminDialogContent>

      <AdminDialogActions sx={{ gap: 1 }}>
        <Box sx={{ flex: 1, display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1, color: 'text.disabled' }}>
          <SearchIcon size={14} />
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>SEO-ready · slug, meta, canonical, OG & author all editable</Typography>
        </Box>
        <Button onClick={onClose} color="inherit" disabled={saving} sx={{ fontWeight: 600 }}>Cancel</Button>

        <Button
          variant="outlined"
          color="warning"
          disabled={saving}
          onClick={() => save('draft')}
          startIcon={<FileText size={15} />}
          sx={{ fontWeight: 700, borderRadius: 2 }}
        >
          Save as draft
        </Button>

        <Button
          onClick={() => save('published')}
          variant="contained"
          color="success"
          disabled={saving}
          startIcon={<Send size={15} />}
          sx={{ px: 3, fontWeight: 700, borderRadius: 2 }}
        >
          {saving ? 'Saving…' : 'Publish Blog'}
        </Button>
      </AdminDialogActions>
    </AdminDialog>
  );
};

export default BlogModal;
